import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Tag } from 'primeng/tag';
import RolloutSampleStatus from '../../../../../types/firmware-rollout/RolloutSampleStatus';
import RolloutSampleStatusDTO from '../../../../../types/firmware-rollout/RolloutSampleStatusDTO';
import PhysicalSample from '../../../../../types/physical-sample/PhysicalSample';
import PhysicalSampleWriteDTO from '../../../../../types/physical-sample/PhysicalSampleWriteDTO';
import SampleHardwareRevision from '../../../../../types/sample-hardware-revision/SampleHardwareRevision';
import { IsProducerDirective } from '../../../directives/is-producer.directive';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { RoleNamePipe } from '../../../pipes/role-name.pipe';
import { FirmwareRolloutService } from '../../../services/firmware-rollout.service';
import { PhysicalSampleService } from '../../../services/physical-sample.service';
import { SampleHardwareRevisionService } from '../../../services/sample-hardware-revision.service';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { normalizePcbVersion } from '../../shared/physical-samples/firmware-upgrade-scope.util';
import {
    UpdatePhysicalSampleDialogComponent
} from '../../shared/physical-samples/update-physical-sample-dialog/update-physical-sample-dialog.component';

@Component({
    selector: 'app-physical-sample-detail',
    templateUrl: './physical-sample-detail.component.html',
    styleUrls: [ './physical-sample-detail.component.scss' ],
    imports: [
        RouterLink,
        PageHeaderComponent,
        UpdatePhysicalSampleDialogComponent,
        Button,
        Tag,
        RoleNamePipe,
        EnumLabelPipe,
        IsProducerDirective
    ]
})
export class PhysicalSampleDetailComponent implements OnInit {
    public rolloutSampleStatus: RolloutSampleStatusDTO | null = null;

    public physicalSample: PhysicalSample | null = null;
    public editablePhysicalSample: PhysicalSampleWriteDTO = new PhysicalSampleWriteDTO({});
    public displayUpdateDialog = false;
    public hardwareRevisions: SampleHardwareRevision[] = [];
    protected readonly RolloutSampleStatus = RolloutSampleStatus;

    constructor(
        private route: ActivatedRoute,
        private physicalSampleService: PhysicalSampleService,
        private firmwareRolloutService: FirmwareRolloutService,
        private sampleHardwareRevisionService: SampleHardwareRevisionService,
        private messageService: MessageService
    ) {
    }

    /**
     * Human-readable label for the sample's current firmware rollout status, or null when the
     * sample has never been part of a rollout.
     */
    public get rolloutStatusLabel(): string | null {
        switch (this.rolloutSampleStatus?.currentStatus) {
            case RolloutSampleStatus.SUCCESS:
                return 'Up to date';
            case RolloutSampleStatus.PENDING:
                return 'Update pending';
            case RolloutSampleStatus.START:
                return 'Update in progress';
            case RolloutSampleStatus.ERROR:
                return 'Update failed';
            default:
                return null;
        }
    }

    public get rolloutStatusSeverity(): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
        switch (this.rolloutSampleStatus?.currentStatus) {
            case RolloutSampleStatus.SUCCESS:
                return 'success';
            case RolloutSampleStatus.PENDING:
                return 'warn';
            case RolloutSampleStatus.START:
                return 'info';
            case RolloutSampleStatus.ERROR:
                return 'danger';
            default:
                return 'secondary';
        }
    }

    /**
     * Whether the sample's reported pcb value matches one of the known hardware revisions.
     * Null while the hardware revisions haven't loaded yet (or the sample has no reported pcb),
     * so the template can avoid flashing a false "unrecognized" warning.
     */
    public get isPcbRecognized(): boolean | null {
        if (!this.physicalSample?.pcb || this.hardwareRevisions.length === 0) {
            return null;
        }
        const normalizedPcb = normalizePcbVersion(this.physicalSample.pcb);
        return this.hardwareRevisions.some(
            revision => normalizePcbVersion(revision.pcbVersion) === normalizedPcb);
    }

    /**
     * Initializes the component
     */
    public ngOnInit(): void {
        this.loadPhysicalSample();
        this.loadRolloutStatus();
        this.loadHardwareRevisions();
    }

    /**
     * Opens the edit dialog
     */
    public openEditDialog(): void {
        this.editablePhysicalSample = new PhysicalSampleWriteDTO(this.physicalSample ?? {});
        this.editablePhysicalSample.sampleId = this.physicalSample?.sample?.id;
        this.editablePhysicalSample.ownerDealershipId = this.physicalSample?.ownerDealership?.id;
        this.editablePhysicalSample.studioId = this.physicalSample?.studio?.id;
        this.displayUpdateDialog = true;
    }

    /**
     * Reloads the physical sample details
     */
    public reloadPhysicalSampleDetails(): void {
        this.loadPhysicalSample();
    }

    /**
     * Confirms the deletion of the physical sample
     */
    public deletePhysicalSample(): void {
        if (!this.physicalSample) {
            return;
        }

        const id = this.physicalSample.id;
        this.physicalSampleService.deletePhysicalSample(id).then(() => {
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Physical sample deleted successfully',
                life: 3000,
            });
            window.history.back(); // Navigate back after deletion
        }).catch((error: any) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: error.message,
                life: 3000,
            });
        });
    }

    /**
     * Loads the physical sample details
     */
    private loadPhysicalSample(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            const idNumber = parseInt(id);
            this.physicalSampleService.getPhysicalSample(idNumber).then((result: PhysicalSample) => {
                this.physicalSample = result;
            }).catch((error: any) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message,
                    life: 3000,
                });
            });
        }
    }

    /**
     * Loads the sample's current firmware rollout status. Rejects (including 404 when no rollout
     * ever targeted the sample) - treated as "no rollout history" rather than an error.
     */
    private loadRolloutStatus(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (!id) {
            return;
        }
        this.firmwareRolloutService.getSampleStatus(parseInt(id))
            .then((result: RolloutSampleStatusDTO) => {
                this.rolloutSampleStatus = result;
            })
            .catch(() => {
                this.rolloutSampleStatus = null;
            });
    }

    /**
     * Loads every known hardware revision, used to check whether the sample's reported pcb value
     * is recognized.
     */
    private loadHardwareRevisions(): void {
        this.sampleHardwareRevisionService.getAllSampleHardwareRevisions()
            .then((result: SampleHardwareRevision[]) => {
                this.hardwareRevisions = result;
            })
            .catch(() => {
                // Best-effort - the raw hardware version is still shown without a match indicator.
            });
    }
}
